import { Link } from 'react-router-dom';

import { REVIEW_HISTORY_LIMIT } from '../api/review.api';
import { historyLine } from '../lib/historyLine';
import { messages } from '@/shared/constants/messages';
import { formatDate } from '@/shared/lib/dates';
import type { Id, ReviewHistoryEntry } from '@/shared/types/domain';

interface ReviewHistoryProps {
  entries: readonly ReviewHistoryEntry[];
  isLoading: boolean;
  currentProfileId: Id | undefined;
}

/**
 * "Decidido nos últimos 30 dias", abaixo da fila (decisão 28).
 *
 * São as decisões de **todos** os revisores no escopo de quem olha, não só as
 * suas — é o ponto: dois pastores que cuidam do mesmo ministério passam a
 * saber o que o outro decidiu, e a fila vazia deixa de ser um beco.
 *
 * Cada linha **navega** (o projeto já está decidido: não há efeito colateral),
 * com a origem `/analise` para a trilha da tela do projeto dizer "Análise".
 */
export function ReviewHistory({ entries, isLoading, currentProfileId }: ReviewHistoryProps) {
  const visible = entries.slice(0, REVIEW_HISTORY_LIMIT);

  return (
    <section className="space-y-3" aria-labelledby="historico-decisoes">
      <h2
        id="historico-decisoes"
        className="text-xs font-medium uppercase tracking-wider text-muted-foreground"
      >
        {messages.review.historyTitle}
      </h2>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">{messages.review.historyEmpty}</p>
      ) : (
        <ul className="divide-y divide-border">
          {visible.map((entry) => (
            <li key={`${entry.project_id}-${entry.created_at}`}>
              {/* ⚠️ **O verbo cai para a segunda linha abaixo de `sm`.** Numa
                  linha só, ele e a data são `shrink-0` e comem os 375px
                  inteiros: o título sobrava com uma letra e meia — "C ajustes
                  solicitados por Elias Ferreira" —, que é o oposto do que a
                  lista existe para dizer. */}
              <Link
                to={`/projetos/${entry.project_id}`}
                state={{ from: '/analise' }}
                className="flex flex-col gap-0.5 rounded px-1 py-2 text-sm transition-colors hover:bg-accent/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:flex-row sm:items-baseline sm:gap-x-3"
              >
                <span className="flex min-w-0 items-baseline gap-2 sm:flex-1">
                  <span className="tabular shrink-0 text-muted-foreground">
                    {formatDate(entry.created_at)}
                  </span>
                  <span className="min-w-0 truncate font-medium">
                    {entry.title ?? 'Sem título'}
                  </span>
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {historyLine(
                    entry.to_status,
                    entry.actor_profile_id,
                    entry.actor_name,
                    currentProfileId,
                  )}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}

      {entries.length > REVIEW_HISTORY_LIMIT ? (
        <p className="text-xs text-muted-foreground">{messages.review.historyTruncated}</p>
      ) : null}
    </section>
  );
}
